import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import DatePicker from "../FormFields/DatePicker";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { l1Columns, storeColumns } from "../Columns/Vouchers/AllVouchers";
import DetailsCard from "../DetailsCard";
import { FaChevronRight } from "react-icons/fa";
import PreviewDocModal from "../../pages/cashDeposits/PreviewDocModal";
import {
  downloadReportsFun,
  getArrayOfSelectedStoresCode,
} from "../../../srcWeb/utils/helpers";
import {
  normalizeVoucherParentRowForStoreColumns,
  resolveVoucherListRowId,
  storeVoucherParentsFromGetRecordsResponse,
} from "../../../srcWeb/utils/dmsStandaloneListUtils";
import { useAuth } from "../../contexts/AuthContext";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
} from "react-router-dom";

const ReworkHistory = ({ status }) => {
  const { data, nonProcessedData, apiCall } = useApi();
  const { apiCall: fileDownloadAtStore } = useApi();
  const { apiCall: fileDownload } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(
    moment(
      searchParams.get("startDate") || moment().subtract({ days: 7 }).toDate()
    ).toDate()
  );
  const [endDate, setEndDate] = useState(
    moment(searchParams.get("endDate") || new Date()).toDate()
  );
  const {
    user: { userLabel = "Store", access_token: accessToken } = {},
    selectedStores,
    storeDropdownState,
  } = useAuth();

  const [tableData, setTableData] = useState(null);
  const [downloadPreviewOpenWithData, setDownloadPreviewOpenWithData] =
    useState({
      isOpen: false,
      file: null,
      filename: "",
    });
  const inFlightRequestKeyRef = useRef(null);
  const lastCompletedRequestKeyRef = useRef(null);

  const storeListRowsFromApi = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) {
      return payload.map(normalizeVoucherParentRowForStoreColumns);
    }
    return storeVoucherParentsFromGetRecordsResponse(payload);
  };

  /** Store REWORK uses storeWiseRecordsResponses — use full API object when `data` is not a plain response. */
  const storeReworkListSource = useMemo(() => {
    if (!userLabel || userLabel !== "Store") return null;
    const isPlainObject =
      data && typeof data === "object" && !Array.isArray(data);
    return isPlainObject ? data : nonProcessedData;
  }, [userLabel, data, nonProcessedData]);

  const mobileStoreReworkRows = useMemo(() => {
    if (Array.isArray(tableData) && tableData.length > 0) {
      return tableData;
    }
    if (userLabel === "Store") {
      return storeListRowsFromApi(storeReworkListSource || {});
    }
    return data?.storeWiseRecordsResponses || [];
  }, [tableData, userLabel, storeReworkListSource, data?.storeWiseRecordsResponses]);

  const storeFetchDependencyKey = useMemo(() => {
    const selectedCode =
      selectedStores?.storeDetails?.storeCode ||
      selectedStores?.storeCode ||
      selectedStores?.value ||
      "";
    const dropdownCount = Array.isArray(storeDropdownState)
      ? storeDropdownState.length
      : storeDropdownState
        ? 1
        : 0;
    return `${selectedCode}|${dropdownCount}`;
  }, [
    selectedStores?.storeDetails?.storeCode,
    selectedStores?.storeCode,
    selectedStores?.value,
    Array.isArray(storeDropdownState) ? storeDropdownState.length : 0,
  ]);

  const onClickChangeData = (data) => {
    setTableData(data);
  };

  const getStoreChildren = (row) => {
    const docs = Array.isArray(row?.documents) ? row.documents : [];
    if (docs.length > 0) return docs;
    return Array.isArray(row?.dataModalRecordsResponse)
      ? row.dataModalRecordsResponse
      : [];
  };

  const voucherApiCall = async (start, end) => {
    const startTs = moment(start).startOf("d").format("YYYY-MM-DD HH:mm:ss");
    const endTs = moment(end || start).endOf("d").format("YYYY-MM-DD HH:mm:ss");
    const storeCodes = getArrayOfSelectedStoresCode(
      selectedStores,
      storeDropdownState
    );
    const requestKey = `${startTs}|${endTs}|${storeCodes.join(",")}`;
    if (inFlightRequestKeyRef.current === requestKey || lastCompletedRequestKeyRef.current === requestKey) {
      return;
    }
    inFlightRequestKeyRef.current = requestKey;
    navigate(
      {
        pathname: window.location.pathname,
        search: createSearchParams({
          startDate: start,
          endDate: end,
        }).toString(),
      },
      { replace: true }
    );
    setTableData(null);
    try {
      await apiCall({
        url: apiUrls.dmsService.getRecords,
        dataToSend: {
          startDate: startTs,
          metadataSchemaId: 4,
          endDate: endTs,
          searchText: "",
          status: "REWORK",
          storeCodes,
        },
        method: "post",
        onSuccess: () => {
          lastCompletedRequestKeyRef.current = requestKey;
        },
      });
    } finally {
      if (inFlightRequestKeyRef.current === requestKey) {
        inFlightRequestKeyRef.current = null;
      }
    }
  };

  useEffect(() => {
    voucherApiCall(startDate, endDate);
  }, [storeFetchDependencyKey, accessToken]);

  useEffect(() => {
    if (userLabel !== "Store") return;
    setTableData(storeListRowsFromApi(storeReworkListSource || {}));
  }, [data, nonProcessedData, userLabel, storeReworkListSource]);

  const onChangeDate = (dates) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      if (!start) return;
      setStartDate(start);
      setEndDate(end || null);
      if (end) voucherApiCall(start, end);
      return;
    }

    if (!dates) return;
    setStartDate(dates);
    setEndDate(dates);
    voucherApiCall(dates, dates);
  };

  const onClickDownloadFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        setDownloadPreviewOpenWithData({
          file: apiData.data,
          filename,
          isOpen: true,
        });
      },
    });
  };

  const onClickViewFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        setDownloadPreviewOpenWithData({
          file: apiData.data,
          filename,
          isOpen: true,
        });
      },
    });
  };


  const downloadVoucher = (data, status) => {
    const st = `${moment(startDate).format("YYYY-MM-DD")} 00:00:00`;
    const et = `${moment(endDate).format("YYYY-MM-DD")} 23:59:59`;
    if (data?.storeName) {
      const getStoreCodeByName = (storeName) => {
        return (
          [storeDropdownState.find((i) => i.name == storeName)?.storeCode] || ""
        );
      };
      let dat = getStoreCodeByName(data.storeName);
      fileDownloadAtStore({
        url: `${apiUrls.dmsService.downloadVoucherStoreLevel}`,
        dataToSend: {
          storeCodes: dat,
          status: status,
          metadataSchemaId: 4,
          endDate: et,
          startDate: st,
        },
        method: "post",
        responseType: "blob",
        responseData: (res) => {
          const { data = "", status = null } = res || {};
          const contentType = res?.headers?.["content-type"];
          const contentDisposition = res?.headers["content-disposition"];
          const fileNameMatch =
            contentDisposition &&
            contentDisposition.match(/filename="(.+)"/);
          const fileName = fileNameMatch
            ? fileNameMatch[1]
            : `voucher_${status}_${moment().format("YYYY-MM-DD")}.xlsx`;

          if (status === 200) {
            downloadReportsFun(data, fileName, contentType);
          }
        },
      });
    }
  };

  const renderColumns = (extraColumns = []) => {
    if (userLabel === "Store") {
      return storeColumns({
        navigate,
        downloadVoucher,
        navigationStateExtras: { type: "Rework" },
      });
    }
    return l1Columns({
      onClickViewFile,
      onClickDownloadFile,
      extraColumns,
      downloadVoucher,
    });
  };

  return (
    <div className="flex flex-col py-2">
      <div className="flex flex-col mb-4 gap-y-3 mt-3">
        <DatePicker
          onChange={onChangeDate}
          dateRange
          startDate={startDate}
          endDate={endDate}
        />
      </div>
      <div>
        <DetailsCard
          showGlobalSearch
          data={mobileStoreReworkRows}
          rows={renderColumns(
            Array.isArray(tableData) && tableData.length > 0
              ? [
                  {
                    Header: "V.ID",
                    accessor: "id",
                    Cell: (rowData) => {
                      const original = rowData?.row?.original ?? rowData;
                      const id = resolveVoucherListRowId(original);
                      const isPendingDocAvail = true;
                      if (!id) return <span>-</span>;

                      return (
                        <div
                          className={`${
                            isPendingDocAvail
                              ? "underline underline-offset-4 cursor-pointer"
                              : ""
                          }`}
                          style={{
                            color: isPendingDocAvail ? "blue" : "black",
                            cursor: isPendingDocAvail ? "pointer" : "default",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isPendingDocAvail && id) {
                              navigate(`/vouchers/${id}`, {
                                state: {
                                  ...original,
                                  type: "Rework",
                                },
                              });
                            }
                          }}
                        >
                          {id}
                        </div>
                      );
                    },
                  },
                ]
              : [
                  {
                    Header: "Store Name",
                    accessor: "storeName",
                    Cell: (rowData) => {
                      const store_name = rowData?.storeName;
                      return (
                        <div
                          className="cursor-pointer underline underline-offset-4"
                          style={{ color: "blue" }}
                          onClick={() =>
                            onClickChangeData(
                              getStoreChildren(rowData).map(
                                normalizeVoucherParentRowForStoreColumns
                              )
                            )
                          }
                        >
                          {store_name}
                        </div>
                      );
                    },
                  },
                ]
          )}
          customRow={(row, rowData) => {
            if (row.Header === "Store Name") {
              return (
                <div
                  className={`min-h-[5rem] bg-[var(--darkGray2)] flex items-center justify-between px-5 text-[var(--whiteColor)] rounded-tl-[1rem] rounded-tr-[1rem]`}
                  onClick={() => {
                    onClickChangeData(
                      getStoreChildren(rowData).map(
                        normalizeVoucherParentRowForStoreColumns
                      )
                    );
                  }}
                >
                  <div className="min-w-[18rem] font-size-medium">
                    {rowData?.[row?.accessor]}
                  </div>

                  <FaChevronRight color="var(--whiteColor)" size={15} />
                </div>
              );
            }

            if (row.Header === "V.ID") {
              let isPendingDocAvail = true;
              const id = resolveVoucherListRowId(
                rowData?.row?.original ?? rowData
              );
              return (
                <div
                  className={`min-h-[5rem] bg-[var(--darkGray2)] flex items-center justify-between px-5 text-[var(--whiteColor)] rounded-tl-[1rem] rounded-tr-[1rem]`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if ((userLabel === "Store" || isPendingDocAvail) && id) {
                      navigate(`/vouchers/${id}`, {
                        state: {
                          ...rowData,
                          type: "Rework",
                        },
                      });
                    }
                  }}
                >
                  <div className="min-w-[18rem] font-size-medium">
                    {id || rowData?.[row?.accessor] || "-"}
                  </div>

                  {userLabel === "Store" || isPendingDocAvail ? (
                    <FaChevronRight color="var(--whiteColor)" size={15} />
                  ) : null}
                </div>
              );
            }
          }}
          sendRowDataInHandler
          isRejectApproveButtonEnable={false}
          isVoucher
        />
      </div>

      <PreviewDocModal
        isModalVisible={downloadPreviewOpenWithData}
        setIsModalVisible={setDownloadPreviewOpenWithData}
      />
    </div>
  );
};

export default ReworkHistory;
